"use client";

import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import {
  Users,
  Plus,
  ChevronDown,
  X,
  Filter,
  Sparkles,
  Zap,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AudienceCriteria,
  FilterGroup,
  FilterGroupLogic,
  AudienceFilter,
} from "../../types/audienceTypes";
import { getFieldById, FILTER_CATEGORIES } from "../../config/filterFields";
import FilterCategoryModal from "../FilterCategorySelector/FilterCategoryModal";

// ============================================================================
// Inline Styles (scoped to component)
// ============================================================================

const inlineStyles = `
  .inline-audience-selector {
    --ias-bg: #f8fafc;
    --ias-bg-dark: #f1f5f9;
    --ias-border: #e2e8f0;
    --ias-border-hover: #cbd5e1;
    --ias-text: #0f172a;
    --ias-text-secondary: #64748b;
    --ias-accent: #f97316;
    --ias-accent-light: #fff7ed;
  }

  .ias-card {
    background: var(--ias-bg);
    border: 1px solid var(--ias-border);
    border-radius: 12px;
    transition: all 0.2s ease;
  }

  .ias-card:hover {
    border-color: var(--ias-border-hover);
  }

  .ias-filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: white;
    border: 1px solid var(--ias-border);
    border-radius: 8px;
    font-size: 13px;
    color: var(--ias-text);
    transition: all 0.2s ease;
  }

  .ias-filter-tag:hover {
    border-color: var(--ias-accent);
    background: var(--ias-accent-light);
  }

  .ias-logic-badge {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .ias-logic-and {
    background: #f3e8ff;
    color: #7c3aed;
  }

  .ias-logic-or {
    background: #e0f2fe;
    color: #0284c7;
  }
`;

// ============================================================================
// Component Props
// ============================================================================

interface InlineAudienceSelectorProps {
  value: AudienceCriteria | null;
  onChange: (criteria: AudienceCriteria | null) => void;
  recipientCount?: number;
  isLoadingCount?: boolean;
}

// ============================================================================
// Helper to format filter for display
// ============================================================================

function formatFilterDisplay(filter: AudienceFilter): string {
  const field = getFieldById(filter.fieldId);
  if (!field) return filter.fieldId;

  let valueDisplay = "";

  if (filter.value === null || filter.value === undefined) {
    valueDisplay = "...";
  } else if (Array.isArray(filter.value)) {
    const valueArray = filter.value as (string | number)[];
    if (valueArray.length === 0) {
      valueDisplay = "...";
    } else if (valueArray.length === 1) {
      const option = field.options?.find((o) => String(o.id) === String(valueArray[0]));
      valueDisplay = option?.label || String(valueArray[0]);
    } else {
      valueDisplay = `${valueArray.length} selected`;
    }
  } else if (typeof filter.value === "object" && "from" in filter.value) {
    valueDisplay = `${filter.value.from} - ${filter.value.to}`;
  } else if (typeof filter.value === "boolean") {
    valueDisplay = filter.value ? "Yes" : "No";
  } else {
    const option = field.options?.find((o) => String(o.id) === String(filter.value));
    valueDisplay = option?.label || String(filter.value);
  }

  return `${field.label}: ${valueDisplay}`;
}

// ============================================================================
// Main Component
// ============================================================================

export function InlineAudienceSelector({
  value,
  onChange,
  recipientCount,
  isLoadingCount = false,
}: InlineAudienceSelectorProps) {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Get total filter count
  const totalFilters = value?.filterGroups.reduce(
    (acc, group) => acc + group.filters.length,
    0
  ) || 0;

  // Handle adding a new filter
  const handleAddFilter = useCallback(
    (fieldId: string) => {
      const newFilter: AudienceFilter = {
        id: uuidv4(),
        fieldId,
        operator: "is_any_of",
        value: null,
      };

      if (!value || value.filterGroups.length === 0) {
        // Create first group
        onChange({
          type: "active",
          groupLogic: "AND",
          filterGroups: [
            {
              id: uuidv4(),
              logic: "AND",
              filters: [newFilter],
            },
          ],
        });
      } else {
        // Add to last group
        const updatedGroups = [...value.filterGroups];
        const lastGroup = { ...updatedGroups[updatedGroups.length - 1] };
        lastGroup.filters = [...lastGroup.filters, newFilter];
        updatedGroups[updatedGroups.length - 1] = lastGroup;

        onChange({
          ...value,
          filterGroups: updatedGroups,
        });
      }

      setIsFilterModalOpen(false);
    },
    [value, onChange]
  );

  // Handle removing a filter
  const handleRemoveFilter = useCallback(
    (groupId: string, filterId: string) => {
      if (!value) return;

      const updatedGroups = value.filterGroups
        .map((group) => {
          if (group.id !== groupId) return group;
          return {
            ...group,
            filters: group.filters.filter((f) => f.id !== filterId),
          };
        })
        .filter((group) => group.filters.length > 0);

      if (updatedGroups.length === 0) {
        onChange(null);
      } else {
        onChange({
          ...value,
          filterGroups: updatedGroups,
        });
      }
    },
    [value, onChange]
  );

  // Toggle group logic
  const handleToggleGroupLogic = useCallback(() => {
    if (!value) return;
    onChange({
      ...value,
      groupLogic: value.groupLogic === "AND" ? "OR" : "AND",
    });
  }, [value, onChange]);

  // Clear all filters
  const handleClearAll = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <>
      <style>{inlineStyles}</style>

      <div className="inline-audience-selector">
        <div className="ias-card overflow-hidden">
          {/* Header */}
          <div
            className={cn(
              "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
              isExpanded ? "bg-[var(--ias-bg-dark)]" : "hover:bg-[var(--ias-bg-dark)]/50"
            )}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[var(--ias-accent)] to-orange-600 flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[var(--ias-text)]">
                    Audience
                  </span>
                  {totalFilters > 0 && (
                    <Badge
                      className="bg-[var(--ias-accent)]/10 text-[var(--ias-accent)] border-0 text-xs"
                      variant="outline"
                    >
                      {totalFilters} {totalFilters === 1 ? "filter" : "filters"}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-[var(--ias-text-secondary)]">
                  {totalFilters === 0 ? (
                    "All contacts (no filters applied)"
                  ) : isLoadingCount ? (
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3 animate-pulse" />
                      Calculating...
                    </span>
                  ) : recipientCount !== undefined ? (
                    <span className="text-[var(--ias-accent)] font-medium">
                      {recipientCount.toLocaleString()} contacts
                    </span>
                  ) : (
                    "Click to add filters"
                  )}
                </div>
              </div>
            </div>

            <ChevronDown
              className={cn(
                "w-5 h-5 text-[var(--ias-text-secondary)] transition-transform",
                isExpanded && "rotate-180"
              )}
            />
          </div>

          {/* Expanded Content */}
          {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                animate={{ height: "auto", opacity: 1 }}
                className="overflow-hidden"
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="px-4 py-4 border-t border-[var(--ias-border)]">
                  {/* Empty State */}
                  {totalFilters === 0 && (
                    <div className="text-center py-6">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[var(--ias-accent)]/10 to-orange-100 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-[var(--ias-accent)]" />
                      </div>
                      <p className="text-sm text-[var(--ias-text-secondary)] mb-4">
                        Segment your audience with filters
                      </p>
                      <Button
                        className="bg-[var(--ias-accent)] hover:bg-[var(--ias-accent)]/90 text-white"
                        onClick={() => setIsFilterModalOpen(true)}
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Filter
                      </Button>
                    </div>
                  )}

                  {/* Filter Tags */}
                  {totalFilters > 0 && value && (
                    <div className="space-y-3">
                      {value.filterGroups.map((group, groupIndex) => (
                        <React.Fragment key={group.id}>
                          {/* Group Logic Between Groups */}
                          {groupIndex > 0 && (
                            <div className="flex items-center justify-center py-1">
                              <button
                                className={cn(
                                  "ias-logic-badge cursor-pointer hover:opacity-80",
                                  value.groupLogic === "AND"
                                    ? "ias-logic-and"
                                    : "ias-logic-or"
                                )}
                                onClick={handleToggleGroupLogic}
                              >
                                {value.groupLogic}
                              </button>
                            </div>
                          )}

                          {/* Filter Group */}
                          <div className="flex flex-wrap items-center gap-2">
                            {group.filters.map((filter, filterIndex) => (
                              <React.Fragment key={filter.id}>
                                {/* Logic Between Filters */}
                                {filterIndex > 0 && (
                                  <span
                                    className={cn(
                                      "ias-logic-badge",
                                      group.logic === "AND"
                                        ? "ias-logic-and"
                                        : "ias-logic-or"
                                    )}
                                  >
                                    {group.logic}
                                  </span>
                                )}

                                {/* Filter Tag */}
                                <motion.div
                                  animate={{ opacity: 1, scale: 1 }}
                                  className="ias-filter-tag group"
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  layout
                                >
                                  <Filter className="w-3 h-3 text-[var(--ias-accent)]" />
                                  <span className="max-w-48 truncate">
                                    {formatFilterDisplay(filter)}
                                  </span>
                                  <button
                                    className="p-0.5 rounded hover:bg-red-100 text-[var(--ias-text-secondary)] hover:text-red-500 transition-colors"
                                    onClick={() =>
                                      handleRemoveFilter(group.id, filter.id)
                                    }
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </motion.div>
                              </React.Fragment>
                            ))}
                          </div>
                        </React.Fragment>
                      ))}

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-2">
                        <Button
                          className="text-[var(--ias-text-secondary)] border-dashed"
                          onClick={() => setIsFilterModalOpen(true)}
                          size="sm"
                          variant="outline"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Filter
                        </Button>
                        <Button
                          className="text-[var(--ias-text-secondary)]"
                          onClick={handleClearAll}
                          size="sm"
                          variant="ghost"
                        >
                          Clear All
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterCategoryModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onSelectField={handleAddFilter}
      />
    </>
  );
}

export default InlineAudienceSelector;
